<script>
  import { onMount, setContext } from 'svelte';
  import { globalHistory, navigate, Route, Router } from 'svelte-navigator';
  import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query';

  import { ActivityManager } from '@client/components/activity-manager';
  import { Banners } from '@client/components/banners';
  import { BillingWarnings } from '@client/components/billing-warnings';
  import { Capability } from '@client/components/capability';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { Managing } from '@client/components/managing';
  import { Notifications } from '@client/components/notifications';
  import { NotificationsAppBadge } from '@client/components/notifications-appbadge';
  import { PushNotifications } from '@client/components/push-notifications';
  import { Refresher } from '@client/components/refresher';
  import { Sidebar, sidebarStore } from '@client/components/sidebar';
  import { Theme } from '@client/components/theme';
  import { VersionUpdate } from '@client/components/version-update';

  import { BizCardsAssignment, BizCardsDesigner, BizCardsRouting, BizCardsStudio } from '@client/pages/bizcards';
  import { BrandKitDesigner, BrandKitRouting, BrandKitStudio } from '@client/pages/brandkit';
  import { ActivityDashboard } from '@client/pages/insights';
  import { Login } from '@client/pages/login';
  import {
    ManageAssets,
    ManageAssetsEdit,
    ManageAuditHistory,
    ManageDomains,
    ManageDomainsEdit,
    ManageDomainSettings,
    ManageGroups,
    ManageGroupsEdit,
    ManageIntegrations,
    ManageIntegrationsEdit,
    ManageUsers,
    ManageUsersEdit
  } from '@client/pages/manage';
  import { MarketingCampaigns, MarketingPerformance, MarketingTargeting } from '@client/pages/marketing';
  import { Settings } from '@client/pages/settings';
  import { PushHistory, SignaturesRouting, SignaturesStudio, SignaturesStudioEdit } from '@client/pages/signatures';
  import { StaffDomains, StaffEBC, StaffWorkflows, StaffWorkflowsRun } from '@client/pages/staff';

  import { DomainService } from '@client/services/domain';
  import { UserService } from '@client/services/user';

  import { appStore } from '@client/stores/app';
  import { refreshDomainList } from '@client/stores/domain-list';
  import { unreadNotifications } from '@client/stores/notifications';
  import { appSettingsStore } from '@client/stores/page-settings';
  import { hasCapability, processUserConfig, userStore } from '@client/stores/user';

  import { adjustElementsForZoom } from '@client/utils/adjust-zoom';
  import { LocalStorage } from '@client/utils/local-storage';

  import { DISPLAY_SIZES } from '@client/enums';

  import { debounce } from '@common/utils/debounce';

  import { APITransport } from '@client/transports/api';
  import { getBootstrap } from './bootstrap';
  import Redirect from './redirect.svelte';
  import { base, themes } from './themes';
  import Tracker from './tracker.svelte';

  const queryClient = new QueryClient();
  const config = getBootstrap();

  const { basepath = '', theme, context } = config;

  if (!config.basepath) {
    config.basepath = '';
  }

  setContext('config', config);

  let mobile = window.innerWidth < 600;
  let sidebarCollapsed = mobile ? true : LocalStorage.get('sidebar')?.collapsed;

  $appStore.mobile = mobile;

  if (sidebarCollapsed === undefined) {
    LocalStorage.create('sidebar', { collapsed: false });
    sidebarCollapsed = false;
  }

  let defaultPathname = `${basepath}/signatures/studio`;
  let collapsed = sidebarCollapsed;
  let interacting = !sidebarCollapsed;
  let initializing = true;
  let prevToken = '';
  let pathname = window.location.pathname.slice(1);
  let isUserLoggedIn = false;
  let authError = null;
  let blockUI = false;
  let showGlobalLoader = false;

  const toggleSidebar = () => {
    collapsed = !collapsed;
    interacting = !collapsed;

    LocalStorage.update('sidebar', collapsed, 'collapsed');

    sidebarStore.set({
      collapsed,
      interacting
    });
  };

  const initialize = async () => {
    initializing = true;

    const currentDomain = LocalStorage.get('domain');
    const { token } = getBootstrap();

    let user;

    if (currentDomain && !context?.source) {
      user = await DomainService.switch(token, currentDomain);
    } else {
      user = await UserService.get(token);

      if (user?.token) {
        LocalStorage.create('domain', user.authDomain);
      }
    }

    // 401 = Unathorized
    // 403 = Email not verified
    if (user?.status === 401 || user?.status === 403 || user?.status === 400) {
      authError = user.message || 'An error occurred while fetching user data.';
      initializing = false;
      return;
    }

    const config = await processUserConfig({ ...user });

    $userStore = { ...config };

    // Not awaited: the switcher's first page is a convenience, not a prerequisite for rendering.
    refreshDomainList();

    prevToken = config.token;

    defaultPathname =
      hasCapability('manage', 'user:activity') && !$userStore.isManaging
        ? `${basepath}/insights/activity`
        : `${basepath}/signatures/studio`;

    const path = window.location.pathname;

    if ($userStore.isManaging && path.indexOf(`${basepath}/insights/activity`) !== -1) {
      navigate(`${basepath}/signatures/studio`);
    }

    $appStore.initialized = true;

    initializing = false;
  };

  onMount(() => {
    const getParentPath = (pathname) => {
      // extract parent path after basepath: basepath/parent/child -> basepath/parent
      if (!pathname || typeof pathname !== 'string') {
        return;
      }

      let pathWithoutBasepath = pathname;
      if (basepath && pathname.startsWith(basepath)) {
        pathWithoutBasepath = pathname.slice(basepath.length);
      }

      const segments = pathWithoutBasepath.split('/').filter(Boolean);
      const parentSegment = segments[0] || '';

      // combine basepath with parent segment
      return basepath ? `${basepath}/${parentSegment}` : `/${parentSegment}`;
    };

    let previousParentPath = getParentPath(window.location.pathname);

    const unlisten = globalHistory.listen((location) => {
      const currentParentPath = getParentPath(location?.pathname);

      // only cancel requests if we're navigating to a different parent section
      if (previousParentPath !== currentParentPath) {
        APITransport.cancelPendingRequests('route-change');
      }

      previousParentPath = currentParentPath;
    });

    if (basepath?.length) {
      if (!window.location.href.includes(basepath)) {
        navigate(basepath);
      }
    }

    if (isUserLoggedIn) {
      initialize();
    } else {
      initializing = false;
    }

    return () => {
      unlisten();
      APITransport.cancelPendingRequests('app-destroy');
    };
  });

  const onToggleClick = () => {
    if (collapsed && !interacting) {
      interacting = true;
      collapsed = false;
    } else {
      interacting = false;
      collapsed = true;
    }
  };

  const onResize = debounce(() => {
    $appStore.mobile = mobile = window.innerWidth <= 600;
    $appStore.tablet = window.innerWidth > 600 && window.innerWidth < 748;
  }, 150);

  const onRefresh = () => {
    setTimeout(() => navigate(window.location.pathname, { replace: true }), 2000);
  };

  $: isUserLoggedIn = $userStore?.token || config.token;
  $: blockUI = initializing || $appStore?.blocking;
  $: showGlobalLoader = blockUI || $appStore?.loading;
  $: showLoaderBackdrop = $appStore.initialized && blockUI;
  $: sidebarToggleIcon = !collapsed && interacting ? 'chevron-left' : 'chevron-right';
  $: sidebarMenuIcon = collapsed && !interacting ? 'menu' : 'x';
  $: if ($userStore && $userStore?.token !== prevToken && prevToken !== '') {
    prevToken = $userStore?.token;
    pathname = window.location.pathname;

    if (
      hasCapability('manage', 'user:activity') &&
      $userStore.isManaging &&
      pathname.indexOf(`${basepath}/insights/activity`) !== -1
    ) {
      pathname = defaultPathname = `${basepath}/signatures/studio`;
    }

    navigate(`${basepath}/redirect`);
  }

  let displayMode = '';

  window.addEventListener('DOMContentLoaded', () => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      displayMode = 'standalone';
    }

    const root = document.documentElement;
    const app = document.getElementById('app');

    if (mobile && displayMode !== 'standalone') {
      root.style.setProperty('--app-height', `${window.innerHeight}px`);
    } else {
      const selected = DISPLAY_SIZES.find(({ value }) => value === $appSettingsStore.displaySize);

      if (selected) {
        root.style.setProperty('--app-height', selected.height);
        root.style.setProperty('--app-zoom', selected.zoom);
        root.classList.remove(...DISPLAY_SIZES.map((item) => item.class));
        root.classList.add(selected.class);
        app.style.zoom = selected.zoom;
      }

      adjustElementsForZoom();
    }
  });

  $: if (mobile) {
    collapsed = true;
    interacting = false;
  }

  $: badgeNotifications = $unreadNotifications ?? [];
</script>

<Theme {base} {themes} {theme}>
  <QueryClientProvider client={queryClient}>
    <Banners>
      <Notifications>
        <Loader show={showGlobalLoader} backdrop={showLoaderBackdrop} fullscreen={blockUI} />
        <NotificationsAppBadge bind:notifications={badgeNotifications} />

        {#if $userStore?.isManaging}
          <Managing domain={$userStore?.domain} />
        {/if}

        {#if authError}
          <div class="auth-error">
            <img src="https://opensense.s3.amazonaws.com/logo-horizontal.svg" alt="Opensense" />
            <div>{authError}</div>
          </div>
        {/if}

        <Refresher {onRefresh} bind:disabled={interacting}>
          <div class="drag-bar" />

          {#if !initializing}
            <BillingWarnings />
            <VersionUpdate />
            <Router primary={false} {basepath}>
              <Tracker />
              <div id="layout">
                {#if !isUserLoggedIn}
                  <Login onSuccess={initialize} />
                {:else}
                  <PushNotifications bind:token={config.token} />
                  {#if config.showActivityOnly || config?.uiSettings?.showActivityOnly}
                    <div id="content">
                      <ActivityDashboard />
                    </div>
                  {:else}
                    <Sidebar bind:collapsed bind:interacting bind:mobile />
                    <button
                      id="sidebar-toggle"
                      class="hide--mobile"
                      on:click={toggleSidebar}
                      class:collapsed={collapsed && !interacting}
                    >
                      <Icon name={sidebarToggleIcon} />
                    </button>
                    <button
                      id="sidebar-toggle-mobile"
                      on:click={onToggleClick}
                      class:collapsed={collapsed && !interacting}
                    >
                      <Icon name={sidebarMenuIcon} />
                    </button>
                    <div id="content" class:sidebar-open={!(collapsed && !interacting)}>
                      <Capability action="manage" subject="user:activity">
                        <Route path="insights/*">
                          <Route>
                            <Redirect path="insights/activity" />
                          </Route>
                          <Route path="activity">
                            <ActivityDashboard />
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="signatures">
                        <Route path="signatures/*">
                          <Route>
                            <Redirect path="signatures/studio" />
                          </Route>
                          <Route path="studio/*">
                            <Route>
                              <SignaturesStudio />
                            </Route>
                            <Route path="new/:template">
                              <SignaturesStudioEdit create />
                            </Route>
                            <Route path=":id">
                              <SignaturesStudioEdit />
                            </Route>
                          </Route>
                          <Route path="pusher">
                            <PushHistory />
                          </Route>
                          <Route path="routing/*">
                            <Route>
                              <SignaturesRouting />
                            </Route>
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="bizcards">
                        <Route path="bizcards/*">
                          <Route>
                            <Redirect path="bizcards/studio" />
                          </Route>
                          <Route path="studio/*">
                            <Route>
                              <BizCardsStudio />
                            </Route>
                            <Route path="edit/:id">
                              <BizCardsDesigner edit />
                            </Route>
                            <Route path="new/:template">
                              <BizCardsDesigner />
                            </Route>
                          </Route>
                          <Route path="assignment/*">
                            <BizCardsAssignment />
                          </Route>
                          <Route path="routing/*">
                            <Route>
                              <BizCardsRouting />
                            </Route>
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="staff">
                        <Route path="brandkit/*">
                          <Route>
                            <Redirect path="brandkit/studio" />
                          </Route>
                          <Route path="studio/*">
                            <Route>
                              <BrandKitStudio />
                            </Route>
                            <Route path="edit/:id">
                              <BrandKitDesigner edit />
                            </Route>
                            <Route path="new/:template">
                              <BrandKitDesigner />
                            </Route>
                          </Route>
                          <Route path="routing/*">
                            <Route>
                              <BrandKitRouting />
                            </Route>
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="marketing">
                        <Route path="marketing/*">
                          <Route>
                            <Redirect path="campaigns" />
                          </Route>
                          <Route path="performance">
                            <MarketingPerformance />
                          </Route>
                          <Route path="campaigns/*">
                            <MarketingCampaigns />
                          </Route>
                          <Route path="targeting/*">
                            <MarketingTargeting />
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="manage">
                        <Route path="manage/*">
                          <Route>
                            <Redirect path="manage/users" />
                          </Route>
                          <Route path="assets/*">
                            <Route>
                              <ManageAssets />
                            </Route>
                            <Route path=":type/:id">
                              <ManageAssetsEdit />
                            </Route>
                          </Route>
                          <Route path="domains/*">
                            <Route>
                              <ManageDomains />
                            </Route>
                            <Route path=":id">
                              <ManageDomainsEdit />
                            </Route>
                          </Route>
                          <Route path="domain-settings/*">
                            <ManageDomainSettings />
                          </Route>
                          <Route path="groups/*">
                            <Route>
                              <ManageGroups />
                            </Route>
                            <Route path=":id">
                              <ManageGroupsEdit />
                            </Route>
                          </Route>
                          <Route path="integrations/*">
                            <Route>
                              <ManageIntegrations />
                            </Route>
                            <Route path=":id">
                              <ManageIntegrationsEdit />
                            </Route>
                          </Route>
                          <Route path="users/*">
                            <Route>
                              <ManageUsers />
                            </Route>
                            <Route path=":id">
                              <ManageUsersEdit />
                            </Route>
                          </Route>
                          <Route path="audit-history">
                            <ManageAuditHistory />
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="staff">
                        <Route path="staff/*">
                          <Route>
                            <Redirect path="staff/domains" />
                          </Route>
                          <Route path="domains/*">
                            <Route>
                              <StaffDomains />
                            </Route>
                            <Route path=":id">
                              <ManageDomainsEdit />
                            </Route>
                          </Route>
                          <Route path="ebc">
                            <StaffEBC />
                          </Route>
                          <Route path="workflows/*">
                            <Route>
                              <StaffWorkflows />
                            </Route>
                            <Route path=":id">
                              <StaffWorkflowsRun />
                            </Route>
                          </Route>
                        </Route>
                      </Capability>

                      <Capability action="manage" subject="user">
                        <Route path="settings">
                          <Settings />
                        </Route>
                      </Capability>

                      <Route path="/redirect">
                        <Redirect path={pathname} />
                      </Route>

                      <Route path="/*">
                        <Redirect path={defaultPathname} />
                      </Route>
                    </div>
                  {/if}
                {/if}
              </div>
            </Router>
          {/if}
        </Refresher>
        <ActivityManager />
      </Notifications>
    </Banners>
  </QueryClientProvider>
</Theme>

<svelte:window on:resize={onResize} />

<svelte:head>
  <script
    defer
    async
    src="https://maps.googleapis.com/maps/api/js?key=AIzaSyDRDvwbQlzjbE3wmgA4zkW9DcpBK9P8wVI&callback=initMap&libraries=visualization,marker&region=US&v=beta&loading=async"
  ></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/socket.io/2.1.1/socket.io.js" crossorigin="anonymous"></script>
  <!-- <script
    src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.9.3/html2pdf.bundle.min.js"
    crossorigin="anonymous"
  ></script> -->
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.11.0/css/flag-icons.min.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@vime/core@^5/themes/default.css" />
</svelte:head>

<style lang="scss" src="./app.scss"></style>
